// @ts-nocheck
// Realtime agent status, grouped by call group — the core of the team-lead
// board. Each group collapses, carries a state-share bar that doubles as a
// filter, and renders either agent tiles or a dense table. Search and the
// state filter are shared across every group.
import React, { useMemo, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { createPortal } from 'react-dom';
import { ChevronDown, LayoutGrid, List, Search } from 'lucide-react';
import { AGENT_STATES, STATE_ORDER, clock, connectTone } from './teamModel';
import { Dot, EmptyNote, HAIRLINE, MiniBar, Panel, TONE, TableShell, Td, Th } from './teamUi';
import SnoopButtons from './SnoopButtons';

const stateRank = (key) => {
  const i = STATE_ORDER.indexOf(key);
  return i === -1 ? STATE_ORDER.length : i;
};
const byStateThenName = (a, b) =>
  stateRank(a.stateKey) - stateRank(b.stateKey) || String(a.name || '').localeCompare(String(b.name || ''));

function StateShareBar({ agents, filter, onFilter }) {
  const total = agents.length || 1;
  const counts = STATE_ORDER.map((key) => ({
    key,
    ...AGENT_STATES[key],
    n: agents.filter((a) => a.stateKey === key).length,
  }));

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="flex h-2 w-[120px] overflow-hidden rounded-full bg-muted">
        {counts.map((c) =>
          c.n === 0 ? null : (
            <span
              key={c.key}
              title={`${c.label}: ${c.n}`}
              className={TONE[c.tone].bg}
              style={{ width: `${(c.n / total) * 100}%`, opacity: c.dim ? 0.55 : 1 }}
            />
          ),
        )}
      </span>
      {counts.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onFilter(filter === c.key ? null : c.key)}
          className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
            filter === c.key ? `${TONE[c.tone].soft} ${TONE[c.tone].text}` : 'text-muted-foreground hover:bg-muted/60'
          }`}
        >
          <Dot tone={c.tone} size={6} dim={c.dim} />
          <span className="tabular-nums">{c.n}</span>
          <span className="hidden sm:inline">{c.label}</span>
        </button>
      ))}
    </div>
  );
}

function AgentTile({ row, snoop, canSnoop, onOpen }) {
  const alert = row.breakInfo?.exceeded;
  return (
    <div
      className={`rounded-xl border p-2.5 text-left transition-colors ${
        alert ? 'border-destructive/40 bg-destructive/5' : `${HAIRLINE} bg-muted/30 hover:bg-muted/50`
      }`}
    >
      <button type="button" onClick={() => onOpen(row)} className="w-full text-left">
        <div className="flex items-start gap-2">
          <span className="relative mt-0.5 shrink-0">
            <Dot tone={row.state.tone} size={8} dim={row.state.dim} />
            {row.stateKey === 'onCall' ? (
              <span className={`absolute inset-0 animate-ping rounded-full ${TONE.call.bg} opacity-60`} />
            ) : null}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12px] font-medium text-foreground">{row.name}</span>
            <span className="block text-[9px] tabular-nums text-muted-foreground">
              {row.extension ? `x${row.extension}` : row.number || '—'}
            </span>
          </span>
        </div>

        <div className="mt-2 flex items-baseline justify-between gap-2">
          <span className={`truncate text-[10px] font-medium ${TONE[row.state.tone].text}`}>
            {/* row.activity is a free-text queue/campaign status straight
              * off the SSE feed (dashboard.status) — unrelated to real
              * login/call state. Preferring it unconditionally could show
              * a stale/unrelated label (e.g. "Offline") next to a dot
              * correctly colored green for a genuinely logged-in agent.
              * Only trust it while actually on a call, where it's real
              * live-call detail; every other state shows the state label
              * that's actually in sync with the dot color. */}
            {row.stateKey === 'onCall' && row.activity ? row.activity : row.state.label}
          </span>
          {row.breakInfo ? (
            <span
              className={`shrink-0 text-[10.5px] font-semibold tabular-nums ${
                row.breakInfo.exceeded ? TONE.crit.text : TONE.warn.text
              }`}
            >
              {row.breakInfo.exceeded ? '+' : ''}
              {clock(Math.abs(row.breakInfo.remaining))}
            </span>
          ) : null}
        </div>

        {row.calls > 0 ? (
          <div className="mt-1.5 flex items-center gap-1.5">
            <MiniBar value={row.connectPct || 0} tone={connectTone(row.connectPct)} width={34} />
            <span className="text-[9px] tabular-nums text-muted-foreground">
              {row.calls} calls · {row.connectPct == null ? '—' : `${row.connectPct}%`}
            </span>
          </div>
        ) : null}
      </button>

      {row.snoop.answered ? (
        <div className={`mt-2 flex items-center justify-end border-t pt-2 ${HAIRLINE}`}>
          <SnoopButtons row={row} snoop={snoop} allowed={canSnoop} compact />
        </div>
      ) : null}
    </div>
  );
}

export default function TeamGroupBoard({
  groups,
  totalAgents,
  stateFilter,
  onStateFilter,
  snoop,
  canSnoop,
  onOpenAgent,
  filterPortalTarget,
}) {
  const [collapsed, setCollapsed] = useState({});
  const [layout, setLayout] = useState('grid');
  const [search, setSearch] = usePersistedState('cti2/TeamGroupBoard:search', '');

  const term = search.trim().toLowerCase();
  const visible = useMemo(
    () =>
      groups.map((g) => ({
        ...g,
        shown: g.agents
          .filter((a) => {
            if (stateFilter && a.stateKey !== stateFilter) return false;
            if (!term) return true;
            return (
              a.name.toLowerCase().includes(term) ||
              String(a.extension || '').includes(term) ||
              String(a.number || '').includes(term)
            );
          })
          .sort(byStateThenName),
      })),
    [groups, stateFilter, term],
  );

  const controls = (
    <>
      <label className={`flex h-8 items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 ${HAIRLINE}`}>
        <Search size={12} className="shrink-0 text-muted-foreground" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search agent…"
          className="w-28 bg-transparent text-[11px] text-foreground outline-none placeholder:text-muted-foreground focus:w-40"
        />
      </label>

      {stateFilter ? (
        <button
          type="button"
          onClick={() => onStateFilter(null)}
          className="whitespace-nowrap rounded-full bg-muted px-2.5 py-1.5 text-[10px] font-medium text-muted-foreground"
        >
          Clear: {AGENT_STATES[stateFilter]?.label} ×
        </button>
      ) : null}

      <div className={`inline-flex rounded-lg border bg-muted/40 p-0.5 ${HAIRLINE}`}>
        {[
          { key: 'grid', Icon: LayoutGrid },
          { key: 'list', Icon: List },
        ].map(({ key, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setLayout(key)}
            title={key === 'grid' ? 'Tile view' : 'Table view'}
            className={`rounded-md p-1.5 transition-colors ${
              layout === key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon size={13} />
          </button>
        ))}
      </div>
    </>
  );

  return (
    <Panel className="overflow-hidden">
      {filterPortalTarget ? createPortal(controls, filterPortalTarget) : null}

      <div className={`flex items-center justify-between gap-2 border-b px-3.5 py-2 ${HAIRLINE}`}>
        <p className="text-[11px] text-muted-foreground">
          {groups.length} group{groups.length === 1 ? '' : 's'} · {totalAgents} agent
          {totalAgents === 1 ? '' : 's'} · red tiles are the ones that need a nudge
        </p>
      </div>

      {groups.length === 0 ? (
        <div className="p-4">
          <EmptyNote>No call groups are reporting agents right now.</EmptyNote>
        </div>
      ) : (
        <div className="divide-y divide-black/[0.07] dark:divide-white/[0.09]">
          {visible.map((g) => {
            const isOpen = !collapsed[g.id];
            const onCall = g.agents.filter((a) => a.stateKey === 'onCall').length;
            const available = g.agents.filter((a) => a.stateKey === 'available').length;
            return (
              <div key={g.id}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3.5 py-2.5">
                  <button
                    type="button"
                    onClick={() => setCollapsed((c) => ({ ...c, [g.id]: isOpen }))}
                    className="flex min-w-0 items-center gap-1.5 text-left"
                  >
                    <ChevronDown
                      size={13}
                      className={`shrink-0 text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`}
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-semibold text-foreground">{g.name}</span>
                      <span className="block text-[10px] tabular-nums text-muted-foreground">
                        {g.agents.length} agents · {onCall} on call · {available} ready
                      </span>
                    </span>
                  </button>
                  <div className="ml-auto">
                    <StateShareBar agents={g.agents} filter={stateFilter} onFilter={onStateFilter} />
                  </div>
                </div>

                {isOpen ? (
                  <div className="px-3.5 pb-3.5">
                    {g.shown.length === 0 ? (
                      <EmptyNote>
                        {term || stateFilter
                          ? 'No agents match the current filter in this group.'
                          : 'No agents in this group.'}
                      </EmptyNote>
                    ) : layout === 'grid' ? (
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
                        {g.shown.map((row) => (
                          <AgentTile key={row.id} row={row} snoop={snoop} canSnoop={canSnoop} onOpen={onOpenAgent} />
                        ))}
                      </div>
                    ) : (
                      <div className={`overflow-hidden rounded-xl border ${HAIRLINE}`}>
                        <TableShell>
                          <thead>
                            <tr>
                              <Th>Agent</Th>
                              <Th>Live</Th>
                              <Th>Today</Th>
                              <Th align="center">Listen</Th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.shown.map((row) => (
                              <tr key={row.id} className="transition-colors hover:bg-muted/40">
                                <Td>
                                  <button type="button" onClick={() => onOpenAgent(row)} className="text-left">
                                    <span className="block truncate text-[12px] font-medium">{row.name}</span>
                                    <span className="block text-[10px] tabular-nums text-muted-foreground">
                                      No. {row.number || '—'} · Ext {row.extension || '—'}
                                    </span>
                                  </button>
                                </Td>
                                <Td>
                                  <span
                                    className={`inline-flex items-center gap-1.5 text-[11px] ${TONE[row.state.tone].text}`}
                                  >
                                    <Dot tone={row.state.tone} size={6} dim={row.state.dim} />
                                    {row.state.label}
                                  </span>
                                  {row.breakInfo ? (
                                    <span
                                      className={`ml-1 text-[10px] tabular-nums ${
                                        row.breakInfo.exceeded ? TONE.crit.text : TONE.warn.text
                                      }`}
                                    >
                                      {row.breakInfo.exceeded ? 'Over ' : 'Left '}
                                      {clock(Math.abs(row.breakInfo.remaining))}
                                    </span>
                                  ) : null}
                                  <span className="block max-w-[220px] truncate text-[10px] text-muted-foreground">
                                    Activity: {row.activity || '—'}
                                  </span>
                                </Td>
                                <Td>
                                  <span className="block text-[11px] tabular-nums">
                                    <span className="text-muted-foreground">Calls </span>
                                    {row.calls}
                                  </span>
                                  <span className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
                                    Connect
                                    <MiniBar
                                      value={row.connectPct || 0}
                                      tone={connectTone(row.connectPct)}
                                      width={40}
                                      showValue={row.connectPct == null ? '—' : `${row.connectPct}%`}
                                    />
                                  </span>
                                </Td>
                                <Td align="center">
                                  <SnoopButtons row={row} snoop={snoop} allowed={canSnoop} compact />
                                </Td>
                              </tr>
                            ))}
                          </tbody>
                        </TableShell>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
